import * as defaultRenderers from "./defaultRenderers";

type TranslationFunction = (...args: any[]) => string;

type WidthConfig = {
  min?: number;
  grow?: number;
};

export interface ColumnConfigBase<
  Entity,
  Key extends keyof Entity,
  FilterValue = any
> {
  label?: string | TranslationFunction;
  resolveValue?: (props: {
    value: Entity[Key];
    t: TranslationFunction;
    row: Entity;
  }) => string;
  render?: (props: {
    value: Entity[Key];
    t: TranslationFunction;
    row: Entity;
    width: number;
  }) => React.ReactNode;
  showInitial?: boolean;
  initialFilterValue?: FilterValue;
  width?: WidthConfig;
}

export interface StringConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<Entity, Key, string> {
  filterPlaceholder?: string | ((t: TranslationFunction) => void);
}

export interface RangeConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<Entity, Key, { min: number; max: number }> {
  autoMinMax?: boolean;
  min?: number;
  max?: number;
}

export interface EnumConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<Entity, Key, string> {
  map: Record<string, string> | ((t: any) => Record<string, string>);
  filterPlaceholder?: string | ((t: TranslationFunction) => void);
}

export interface BoolConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<Entity, Key, boolean> {}

export interface DateConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<
    Entity,
    Key,
    { start: string | Date; end: string | Date }
  > {
  selectType?: "single" | "range";
  filterPlaceholder?: string | ((t: TranslationFunction) => void);
}

type Tag = {
  id: string;
  name: string;
};

export interface TagsConfig<Entity = {}, Key extends keyof Entity = any>
  extends ColumnConfigBase<Entity, Key, Tag[]> {
  filterPlaceholder?: string | ((t: TranslationFunction) => void);
}

export type ColumnType = "string" | "range" | "enum" | "bool" | "date" | "tags";

export type ColumnConfig = {
  type: ColumnType;
  key: string | number | symbol;
  [key: string]: any;
};

const DEFAULT_COLUMN_CONFIG = {
  showInitial: true
};

type Payload<Entity> = {
  string: ColumnConfigurer<
    keyof Entity,
    StringConfig<Entity, keyof Entity>,
    Entity
  >;
  range: ColumnConfigurer<
    keyof Entity,
    RangeConfig<Entity, keyof Entity>,
    Entity
  >;
  enum: ColumnConfigurer<
    keyof Entity,
    EnumConfig<Entity, keyof Entity>,
    Entity
  >;
  bool: ColumnConfigurer<
    keyof Entity,
    BoolConfig<Entity, keyof Entity>,
    Entity
  >;
  date: ColumnConfigurer<
    keyof Entity,
    DateConfig<Entity, keyof Entity>,
    Entity
  >;
  tags: ColumnConfigurer<
    keyof Entity,
    TagsConfig<Entity, keyof Entity>,
    Entity
  >;
  __get: () => any[];
};

type ColumnConfigurer<K, C, Entity> = (key: K, settings?: C) => Payload<Entity>;

function createConfigurer<K, C, Entity>(
  type: ColumnType,
  config: any[],
  payload: Payload<Entity>,
  defaults: any = {}
): ColumnConfigurer<K, C, Entity> {
  return function x(key: K, settings: C = {} as any) {
    config.push({
      ...DEFAULT_COLUMN_CONFIG,
      ...settings,
      ...defaults,
      key,
      type
    });

    return payload;
  };
}

export default function tableConfig<Entity = {}>() {
  const config: Array<Partial<ColumnConfig>> = [];

  const payload = {
    __get: () => config
  } as Payload<Entity>;

  payload.string = createConfigurer("string", config, payload, {
    initialFilterValue: ""
  });
  payload.range = createConfigurer("range", config, payload, {
    autoMinMax: false,
    min: 0,
    max: 100,
    initialFilterValue: {
      min: 0,
      max: 100
    }
  });
  payload.enum = createConfigurer("enum", config, payload, {
    initialFilterValue: null
  });
  payload.bool = createConfigurer("bool", config, payload, {
    initialFilterValue: true
  });
  payload.date = createConfigurer("date", config, payload, {
    selectType: "range",
    render: defaultRenderers.date
  });
  payload.tags = createConfigurer("tags", config, payload, {
    render: defaultRenderers.tags,
    initialFilterValue: []
  });

  return payload;
}
